import type { DnaKey, DnaProfile, QuizQuestion, Destination } from '@/types'

// ─── DNA Profiles ─────────────────────────────────────────────────────────────

export const DNA_PROFILES: Record<DnaKey, DnaProfile> = {
  '🎢': {
    key: '🎢',
    name: 'Thrill-Seeking Family',
    headline: "You're Thrill-Seekers!",
    description: "Your family lives for the BIG moments — rollercoasters, waterslides and shows that leave the kids buzzing for weeks on end. You don't just go on holiday. You go all in.",
    heroBg: 'linear-gradient(135deg, #FFF0E8, #FFE4CC)',
    heroBorder: '#FFD4AA',
    accentColor: '#FF6B2B',
  },
  '🦁': {
    key: '🦁',
    name: 'Wild Explorer Family',
    headline: "You're Wild Explorers!",
    description: "Your children are genuinely curious about the natural world. You want holidays where lions, sloths and whale sharks are the main attraction — not a screen in sight.",
    heroBg: 'linear-gradient(135deg, #F0FFF4, #D4F5E0)',
    heroBorder: '#A8E6C4',
    accentColor: '#06D6A0',
  },
  '🏖': {
    key: '🏖',
    name: 'Beach & Sun Family',
    headline: "You're Sun-Chasers!",
    description: 'Your family finds its perfect rhythm by the sea. Long lazy days, happy kids building sandcastles, gelato at sunset — evenings so good nobody wants them to end.',
    heroBg: 'linear-gradient(135deg, #E8F7FF, #CCF0FF)',
    heroBorder: '#AAE0FF',
    accentColor: '#00B4D8',
  },
  '🏯': {
    key: '🏯',
    name: 'Culture-Curious Family',
    headline: "You're Culture Explorers!",
    description: "You believe travel is the best classroom on earth. Your children will learn more in one week abroad than in a whole school term — and they'll love every minute of it.",
    heroBg: 'linear-gradient(135deg, #F5F0FF, #E4CCFF)',
    heroBorder: '#D4AAFF',
    accentColor: '#7B2FBE',
  },
}

// ─── Quiz Questions ───────────────────────────────────────────────────────────

export const QUIZ_QUESTIONS: QuizQuestion[] = [
  {
    intro: `I'm going to find your perfect trip by discovering <em>how your family travels</em> when you're all together.`,
    question: `The school holidays just started — six whole days, no plans yet. <strong>What does your dream family trip look like?</strong>`,
    options: [
      { emoji: '🎢', label: 'Theme parks, waterslides and non-stop fun for the kids' },
      { emoji: '🦁', label: 'A real wild adventure — safari, nature, the great outdoors' },
      { emoji: '🏖', label: 'Sun, sea and a beach the kids can play on all day long' },
      { emoji: '🏯', label: 'Exploring somewhere new — castles, markets, local food' },
    ],
  },
  {
    intro: `Love it! Now — <strong>think about your kids</strong> on your last brilliant holiday together. What made them the happiest?`,
    options: [
      { emoji: '🌊', label: 'Splashing in the sea or pool for hours on end' },
      { emoji: '🐘', label: 'Spotting real animals up close — wild ones' },
      { emoji: '🎠', label: 'Rides, shows and things that felt genuinely magical' },
      { emoji: '🔍', label: 'Discovering something — a cave, a ruin, a secret spot' },
    ],
  },
  {
    intro: `<strong>Brilliant answer!</strong> Now as a parent — what matters MOST to you when booking a family holiday?`,
    options: [
      { emoji: '🛡', label: 'Safety and ease — child-friendly, stress-free to navigate' },
      { emoji: '💷', label: 'Real value — making every penny count for the family' },
      { emoji: '😴', label: 'The right pace — rest time, not rushing everywhere' },
      { emoji: '📸', label: 'Unforgettable moments — memories the kids keep forever' },
    ],
  },
  {
    intro: `<strong>Almost there!</strong> What does a perfect family holiday day look like from the moment everyone wakes up?`,
    options: [
      { emoji: '🌅', label: 'Early birds — up early, full day, everyone sleeps well' },
      { emoji: '🥐', label: 'Slow start — lazy breakfast, relaxed afternoon' },
      { emoji: '⚡', label: "We follow the kids' energy — full tilt all the way" },
      { emoji: '🗺', label: 'Mix of planned activities and free time to explore' },
    ],
  },
  {
    intro: `Last one! <strong>Picture your children ten years from now</strong> talking about your best holiday ever. Which memory would they describe?`,
    options: [
      { emoji: '🐘', label: 'The day we saw elephants and lions in the wild' },
      { emoji: '🎆', label: 'That magical night at the theme park with fireworks' },
      { emoji: '🏄', label: 'When we all learned to surf or snorkel together' },
      { emoji: '🍕', label: 'Getting wonderfully lost and finding a perfect local restaurant' },
    ],
  },
]

// ─── Destination Data — with real affiliate networks ──────────────────────────

const DESTINATIONS: Record<DnaKey, Destination[]> = {
  '🎢': [
    {
      id: 'orlando',
      name: 'Orlando',
      country: 'USA',
      flag: '🇺🇸',
      emoji: '🎢',
      gradient: 'linear-gradient(135deg, #1a3a1a, #2d5a2d)',
      saving: '£310',
      ageRange: 'Ages 3–12',
      description: 'The theme park capital of the world. Walt Disney World, Universal Studios, LEGOLAND — kids 3–12 are in absolute heaven.',
      imageQuery: 'Orlando Disney castle theme park',
      facts: [
        { value: '12+', label: 'Theme parks in one trip' },
        { value: '4', label: 'Disney parks alone' },
        { value: '75°F', label: 'Average daytime temp' },
        { value: 'FREE', label: 'Entry for under-3s' },
      ],
      loveIt: [
        'Early Park Entry means shorter queues before breakfast',
        'Kids under 3 go free at almost every park',
        'Genie+ and Lightning Lane cut waiting time in half',
        'Free Disney buses mean you never need to hire a car',
      ],
      stats: { 'Theme parks': '12+', 'Under-3s': 'FREE', 'Best stay': '7 days' },
      deals: [
        {
          icon: '✈️', title: 'Return flights from Gatwick',
          detail: 'British Airways direct · 1 checked bag per person included',
          saving: 'Save £85',
          network: 'skyscanner', category: 'flight',
          baseUrl: 'https://www.skyscanner.net/transport/flights/lond/orla/',
          commissionModel: 'cpc', commissionValue: 0.65,
        },
        {
          icon: '🏨', title: 'Disney on-site family hotel',
          detail: 'Park Early Entry included · Breakfast package available',
          saving: 'Save £60/night',
          network: 'booking_com', category: 'hotel',
          baseUrl: 'https://www.booking.com/searchresults.html?ss=Walt+Disney+World+Resort+Hotels',
          commissionModel: 'revenue_share', commissionValue: 0.04,
        },
        {
          icon: '🎡', title: 'Walt Disney World 5-day family pass',
          detail: 'All 4 parks · Memory Maker photos included',
          saving: 'Save £120',
          network: 'get_your_guide', category: 'activity',
          baseUrl: 'https://www.getyourguide.com/orlando-l191/walt-disney-world-tickets',
          commissionModel: 'revenue_share', commissionValue: 0.08,
        },
        {
          icon: '⚡', title: 'Universal Express family pass',
          detail: 'Skip all queues · Kids under 1.2m free',
          saving: '30% off',
          network: 'viator', category: 'activity',
          baseUrl: 'https://www.viator.com/Orlando/d672-ttd/Universal-Studios',
          commissionModel: 'revenue_share', commissionValue: 0.08,
        },
      ],
      stops: [
        { time: 'Day 1', name: 'Magic Kingdom 🏰', description: 'Start here. Castle, Mickey, Dumbo and the most magical fireworks show on earth.', familyTag: '⭐ Perfect for under-10s' },
        { time: 'Day 2', name: 'EPCOT 🌍', description: "Frozen Ever After, 11 countries to explore, kids' passport stamp trail.", familyTag: '🌍 Fun for all ages' },
        { time: 'Day 3', name: 'Hollywood Studios ⚡', description: "Star Wars: Galaxy's Edge and Toy Story Land — older kids go absolutely wild.", familyTag: '🚀 Best for ages 7–12' },
        { time: 'Day 4', name: 'Animal Kingdom 🦁', description: 'Real giraffes and lions on Kilimanjaro Safari at dawn. Then Avatar: Flight of Passage.', familyTag: '🦁 Wildlife meets thrills' },
        { time: 'Day 5', name: 'LEGOLAND Florida 🧱', description: 'Creative, hands-on and calmer — brilliant for under-10s after the Disney intensity.', familyTag: '🧱 Great for under 10s' },
      ],
    },
    {
      id: 'singapore',
      name: 'Singapore',
      country: 'Singapore',
      flag: '🇸🇬',
      emoji: '🌆',
      gradient: 'linear-gradient(135deg, #1a2a3a, #2d4a6a)',
      saving: '£195',
      ageRange: 'Ages 4–12',
      description: "The world's safest city for families. Universal Studios, Gardens by the Bay and the best waterpark in Asia.",
      imageQuery: 'Singapore Gardens by the Bay supertree',
      facts: [
        { value: '#1', label: 'Safest city for families' },
        { value: '40+', label: 'Family attractions' },
        { value: '18hrs', label: 'Direct flight time' },
        { value: '30°C', label: 'Average temperature' },
      ],
      loveIt: [
        'Spotless, stroller-friendly streets everywhere you go',
        'World-class public transport kids find genuinely fun',
        'Hawker centres mean fussy eaters always find something',
        'English widely spoken — zero language stress for parents',
      ],
      stats: { 'Safety': 'World #1', 'Family attractions': '40+', 'Best stay': '6 days' },
      deals: [
        {
          icon: '✈️', title: 'Return flights from London',
          detail: 'Singapore Airlines direct · In-flight kids meals',
          saving: 'Save £110',
          network: 'skyscanner', category: 'flight',
          baseUrl: 'https://www.skyscanner.net/transport/flights/lond/sin/',
          commissionModel: 'cpc', commissionValue: 0.65,
        },
        {
          icon: '🏨', title: 'Sentosa Island family resort',
          detail: 'Universal Studios on doorstep · Kids club',
          saving: 'Save £55/night',
          network: 'booking_com', category: 'hotel',
          baseUrl: 'https://www.booking.com/searchresults.html?ss=Sentosa+Island+Singapore',
          commissionModel: 'revenue_share', commissionValue: 0.04,
        },
        {
          icon: '🎡', title: 'Universal Studios Singapore 2-day pass',
          detail: 'Family ticket · Priority lanes included',
          saving: 'Save £65',
          network: 'klook', category: 'activity',
          baseUrl: 'https://www.klook.com/activity/8-universal-studios-singapore-admission-ticket/',
          commissionModel: 'revenue_share', commissionValue: 0.06,
        },
        {
          icon: '🌿', title: 'Gardens by the Bay family bundle',
          detail: 'SuperTree light show · Cloud Forest entry',
          saving: '20% off',
          network: 'get_your_guide', category: 'activity',
          baseUrl: 'https://www.getyourguide.com/singapore-l6/gardens-by-the-bay-tickets',
          commissionModel: 'revenue_share', commissionValue: 0.08,
        },
      ],
      stops: [
        { time: 'Day 1', name: 'Universal Studios Singapore 🎢', description: '12 themed zones, family-friendly rides and iconic coasters for bigger kids.', familyTag: '🎢 All ages covered' },
        { time: 'Day 2', name: 'Gardens by the Bay 🌿', description: "Children's water play garden and the magical SuperTree light show at night.", familyTag: '✨ Magical for all ages' },
        { time: 'Day 3', name: 'Adventure Cove Waterpark 🌊', description: 'Lazy river, wave pool and the family raft ride. A brilliant full day.', familyTag: '🌊 Best waterpark in Asia' },
        { time: 'Day 4', name: 'Singapore Zoo & Night Safari 🦁', description: 'Free-roaming animals in rainforest. The night safari is unmissable.', familyTag: '🦁 World-class for families' },
        { time: 'Day 5', name: 'S.E.A. Aquarium 🐠', description: "One of the world's largest aquariums, then cable car over Sentosa.", familyTag: '🐠 Brilliant for under-10s' },
      ],
    },
    {
      id: 'dubai',
      name: 'Dubai',
      country: 'UAE',
      flag: '🇦🇪',
      emoji: '🏙',
      gradient: 'linear-gradient(135deg, #2a1a1a, #5a3a1a)',
      saving: '£220',
      ageRange: 'Ages 5–12',
      description: 'Desert safaris, indoor skiing, world-class waterparks and the tallest building on earth — extraordinary for families.',
      imageQuery: 'Dubai Burj Khalifa skyline desert',
      facts: [
        { value: '828m', label: 'Height of Burj Khalifa' },
        { value: '6+', label: 'World-class waterparks' },
        { value: '7hrs', label: 'Direct flight from UK' },
        { value: '365', label: 'Days of sunshine a year' },
      ],
      loveIt: [
        'Indoor snow at Ski Dubai — a genuine jaw-drop for kids',
        'Beach clubs with shallow, calm, lifeguarded waters',
        'Malls double as air-conditioned entertainment on hot days',
        'Extremely low crime — parents can properly relax',
      ],
      stats: { 'Waterparks': '6+', 'Year-round sun': 'Yes', 'Best stay': '6 days' },
      deals: [
        {
          icon: '✈️', title: 'Return flights from London',
          detail: 'Emirates direct · Kids eat free in-flight',
          saving: 'Save £90',
          network: 'skyscanner', category: 'flight',
          baseUrl: 'https://www.skyscanner.net/transport/flights/lond/dxb/',
          commissionModel: 'cpc', commissionValue: 0.65,
        },
        {
          icon: '🏨', title: 'Beach resort with kids club',
          detail: 'Full board available · Private beach access',
          saving: 'Save £75/night',
          network: 'booking_com', category: 'hotel',
          baseUrl: 'https://www.booking.com/searchresults.html?ss=Dubai+Beach+Resort+family',
          commissionModel: 'revenue_share', commissionValue: 0.04,
        },
        {
          icon: '🌊', title: 'Wild Wadi Waterpark family day',
          detail: 'All 22 rides · Height-appropriate zones',
          saving: 'Save £45',
          network: 'get_your_guide', category: 'activity',
          baseUrl: 'https://www.getyourguide.com/dubai-l173/wild-wadi-waterpark-tickets',
          commissionModel: 'revenue_share', commissionValue: 0.08,
        },
        {
          icon: '🎿', title: 'Ski Dubai family snow day',
          detail: 'Full kit hire included · Penguin parade tickets',
          saving: '15% off',
          network: 'klook', category: 'activity',
          baseUrl: 'https://www.klook.com/activity/ski-dubai-tickets/',
          commissionModel: 'revenue_share', commissionValue: 0.06,
        },
      ],
      stops: [
        { time: 'Day 1', name: 'Wild Wadi Waterpark 🌊', description: '22 interconnected rides in the shadow of the Burj Al Arab. Kids go absolutely wild.', familyTag: '🌊 All ages' },
        { time: 'Day 2', name: 'Desert Safari 🐪', description: 'Dune bashing, camel rides, sandboarding and a Bedouin dinner under the stars.', familyTag: '🐪 Unforgettable family memory' },
        { time: 'Day 3', name: 'Ski Dubai ⛷', description: 'Real snow inside a shopping mall. Kids ski, sled or meet the resident penguins.', familyTag: '⛷ Magical for children' },
        { time: 'Day 4', name: 'Dubai Frame & Gold Souk ✨', description: 'Glass sky-bridge with city views below, then the glittering gold souk.', familyTag: '✨ Cool for curious kids' },
        { time: 'Day 5', name: 'Burj Khalifa — At The Top 🏙', description: "Lift to the 124th floor. Views that genuinely take kids' breath away.", familyTag: '🏙 The ultimate wow moment' },
      ],
    },
  ],
  '🦁': [
    {
      id: 'kenya',
      name: 'Kenya',
      country: 'Kenya',
      flag: '🇰🇪',
      emoji: '🦁',
      gradient: 'linear-gradient(135deg, #2a2a1a, #4a3a10)',
      saving: '£280',
      ageRange: 'Ages 6–12',
      description: 'The Masai Mara at sunrise. Real lions, elephants, giraffes — not behind glass. A safari changes a child forever.',
      imageQuery: 'Masai Mara Kenya safari lions sunrise',
      facts: [
        { value: '5', label: 'Big Five all present' },
        { value: '1,500km²', label: 'Size of the Masai Mara' },
        { value: '6+', label: 'Recommended minimum age' },
        { value: '25°C', label: 'Average daytime temp' },
      ],
      loveIt: [
        'Open-sided 4WDs mean kids see everything, no glass in the way',
        'Family camps run junior ranger programmes for under-12s',
        'Dawn game drives are timed around kids’ natural wake time',
        'A once-in-a-childhood memory no theme park can match',
      ],
      stats: { 'Big Five': 'All present', 'Best age': '6–12', 'Trip length': '7 days' },
      deals: [
        {
          icon: '✈️', title: 'Return flights from London',
          detail: 'Kenya Airways direct to Nairobi · Kids fare available',
          saving: 'Save £130',
          network: 'skyscanner', category: 'flight',
          baseUrl: 'https://www.skyscanner.net/transport/flights/lond/nbo/',
          commissionModel: 'cpc', commissionValue: 0.65,
        },
        {
          icon: '🏕', title: 'Family tented safari camp',
          detail: 'All meals · Game drives · Child wildlife guides',
          saving: 'Save £90/night',
          network: 'booking_com', category: 'hotel',
          baseUrl: 'https://www.booking.com/searchresults.html?ss=Masai+Mara+family+camp',
          commissionModel: 'revenue_share', commissionValue: 0.04,
        },
        {
          icon: '🦒', title: 'Giraffe Centre & Elephant Orphanage',
          detail: 'Hand-feed giraffes · Adopt-an-elephant certificate',
          saving: 'Save £32/family',
          network: 'viator', category: 'activity',
          baseUrl: 'https://www.viator.com/Nairobi/d4420-ttd/Giraffe-Centre',
          commissionModel: 'revenue_share', commissionValue: 0.08,
        },
        {
          icon: '🎈', title: 'Masai Mara hot air balloon safari',
          detail: 'Dawn flight · Safe for ages 6+ · Champagne breakfast',
          saving: 'Save £60/person',
          network: 'get_your_guide', category: 'activity',
          baseUrl: 'https://www.getyourguide.com/masai-mara-l97680/balloon-safari',
          commissionModel: 'revenue_share', commissionValue: 0.08,
        },
      ],
      stops: [
        { time: 'Dawn', name: 'Masai Mara game drive 🦁', description: 'Open 4WD at dawn. Lions hunting, elephants at the river, cheetahs on the plain.', familyTag: '🦁 Life-changing for children' },
        { time: 'Morning', name: 'Giraffe Centre, Nairobi 🦒', description: 'Hand-feed endangered Rothschild giraffes from a raised platform. Under-10s are mesmerised.', familyTag: '🦒 Best for younger kids' },
        { time: 'Afternoon', name: 'David Sheldrick Elephant Orphanage 🐘', description: 'Watch baby elephants play in mud and learn their rescue stories.', familyTag: '🐘 Emotional & educational' },
        { time: 'Day 3', name: 'Hot air balloon safari 🎈', description: 'Float above the Mara at sunrise, spotting herds from the air. Safe for kids 6+.', familyTag: '🎈 Unmissable (age 6+)' },
        { time: 'Evening', name: 'Maasai village visit 🌍', description: 'Meet a local Maasai family, learn the jumping dance, explore another world entirely.', familyTag: '🌍 Cultural immersion for kids' },
      ],
    },
    {
      id: 'costarica',
      name: 'Costa Rica',
      country: 'Costa Rica',
      flag: '🇨🇷',
      emoji: '🌿',
      gradient: 'linear-gradient(135deg, #1a3a1a, #2a5a2a)',
      saving: '£195',
      ageRange: 'Ages 5–12',
      description: "Zip-lining through rainforest canopy, spotting sloths and toucans. Nature's own adventure playground for families.",
      imageQuery: 'Costa Rica rainforest waterfall',
      facts: [
        { value: '500k+', label: 'Known wildlife species' },
        { value: '30+', label: 'National parks & reserves' },
        { value: '5%', label: 'Of the world’s biodiversity' },
        { value: '5+', label: 'Recommended minimum age' },
      ],
      loveIt: [
        'Child-sized zip-line harnesses from age 5 upward',
        'Sloth encounters that genuinely quiet down excitable kids',
        'No visas, jabs or malaria tablets needed for most trips',
        'Eco-lodges built for exactly this kind of family adventure',
      ],
      stats: { 'Wildlife species': '500,000+', 'National parks': '30+', 'Best stay': '8 days' },
      deals: [
        {
          icon: '✈️', title: 'Return flights from London',
          detail: 'Via Miami · British Airways codeshare',
          saving: 'Save £100',
          network: 'skyscanner', category: 'flight',
          baseUrl: 'https://www.skyscanner.net/transport/flights/lond/sjo/',
          commissionModel: 'cpc', commissionValue: 0.65,
        },
        {
          icon: '🏡', title: 'Eco-lodge in the rainforest',
          detail: 'Breakfast included · Wildlife guide on site',
          saving: 'Save £45/night',
          network: 'booking_com', category: 'hotel',
          baseUrl: 'https://www.booking.com/searchresults.html?ss=Arenal+Costa+Rica+eco+lodge',
          commissionModel: 'revenue_share', commissionValue: 0.04,
        },
        {
          icon: '⚡', title: 'Monteverde zip-line family experience',
          detail: 'Cloud forest canopy · Child harnesses from age 5',
          saving: 'Save £30/family',
          network: 'viator', category: 'activity',
          baseUrl: 'https://www.viator.com/Monteverde/d22528-ttd/zip-line',
          commissionModel: 'revenue_share', commissionValue: 0.08,
        },
        {
          icon: '🦥', title: 'Sloth Sanctuary & Monkey tour',
          detail: 'Family guided visit · Rehabilitation centre',
          saving: 'Save £30/family',
          network: 'get_your_guide', category: 'activity',
          baseUrl: 'https://www.getyourguide.com/costa-rica-l108/sloth-sanctuary-tour',
          commissionModel: 'revenue_share', commissionValue: 0.08,
        },
      ],
      stops: [
        { time: 'Day 1', name: 'Arenal Volcano & hot springs 🌋', description: 'Hike gentle trails around an active volcano, then soak in natural thermal pools at sunset.', familyTag: '🌋 Dramatic & safe for kids' },
        { time: 'Day 2', name: 'Monteverde zip-lining ⚡', description: 'Fly through the cloud forest canopy — child-sized harnesses available from age 5.', familyTag: '⚡ Thrilling (age 5+)' },
        { time: 'Day 3', name: 'Sloth Sanctuary 🦥', description: 'Meet two-toed and three-toed sloths up close. Kids can hold a baby sloth with guidance.', familyTag: '🦥 Kids go absolutely wild' },
        { time: 'Day 4', name: 'Manuel Antonio National Park 🐒', description: "Monkeys on the trails, sloths in the trees, and one of Costa Rica's most stunning beaches.", familyTag: '🐒 Wildlife on the beach' },
        { time: 'Day 5', name: 'Family white-water rafting 🌊', description: 'Grade 2–3 family rafting on the Pacuare River — safe, exhilarating and unforgettable.', familyTag: '🌊 Family adventure (age 7+)' },
      ],
    },
    {
      id: 'newzealand',
      name: 'New Zealand',
      country: 'New Zealand',
      flag: '🇳🇿',
      emoji: '🏔',
      gradient: 'linear-gradient(135deg, #1a2a3a, #2a4a6a)',
      saving: '£240',
      ageRange: 'Ages 7–12',
      description: 'Glowworm caves, Hobbiton, whale watching and jaw-dropping landscapes — epic for adventurous families.',
      imageQuery: 'New Zealand Milford Sound',
      facts: [
        { value: '2', label: 'Hobbiton movie set doors' },
        { value: '99%', label: 'Chance of whale sightings' },
        { value: '10', label: 'Recommended trip length (days)' },
        { value: '7+', label: 'Ideal minimum age' },
      ],
      loveIt: [
        'Glowworm caves feel like stepping into actual magic',
        'Genuinely one of the safest countries on earth to drive',
        'Adventure activities are graded and supervised for kids',
        'Landscapes so dramatic even screen-obsessed teens look up',
      ],
      stats: { 'Ideal age': '7–12', 'Nature activities': 'Endless', 'Best stay': '10 days' },
      deals: [
        {
          icon: '✈️', title: 'Return flights from London',
          detail: 'Air New Zealand direct · Kids meals included',
          saving: 'Save £150',
          network: 'skyscanner', category: 'flight',
          baseUrl: 'https://www.skyscanner.net/transport/flights/lond/akl/',
          commissionModel: 'cpc', commissionValue: 0.65,
        },
        {
          icon: '🏨', title: 'Family lodge in Queenstown',
          detail: 'Breakfast · Kids activities programme',
          saving: 'Save £50/night',
          network: 'booking_com', category: 'hotel',
          baseUrl: 'https://www.booking.com/searchresults.html?ss=Queenstown+family+lodge',
          commissionModel: 'revenue_share', commissionValue: 0.04,
        },
        {
          icon: '🧝', title: 'Hobbiton Movie Set family tour',
          detail: "Guided · 2 hours · Lunch at Shire's Rest",
          saving: 'Save £35/family',
          network: 'viator', category: 'activity',
          baseUrl: 'https://www.viator.com/Matamata/d50046-ttd/Hobbiton-movie-set-tour',
          commissionModel: 'revenue_share', commissionValue: 0.08,
        },
        {
          icon: '🌌', title: 'Waitomo glowworm cave boat tour',
          detail: 'Underground river · Magical for all ages',
          saving: 'Save £20/family',
          network: 'get_your_guide', category: 'activity',
          baseUrl: 'https://www.getyourguide.com/waitomo-l2939/glowworm-caves-tour',
          commissionModel: 'revenue_share', commissionValue: 0.08,
        },
      ],
      stops: [
        { time: 'Day 1', name: 'Waitomo Glowworm Caves 🌌', description: 'Drift through underground caves lit by thousands of glowworms. Completely otherworldly.', familyTag: '🌌 Genuinely magical' },
        { time: 'Day 2', name: 'Hobbiton Movie Set 🧝', description: 'Walk through the real Shire. The green door, the party field — children are completely enchanted.', familyTag: '🧝 Perfect for young imaginations' },
        { time: 'Day 3', name: 'Queenstown gondola & luge 🎿', description: 'Cable car to the summit, then ride the luge track down as fast as you dare.', familyTag: '🎿 All ages love it' },
        { time: 'Day 4', name: 'Milford Sound cruise 🐬', description: "Sail through the world's most dramatic fiord — dolphins, seals and towering waterfalls.", familyTag: '🐬 Awe-inspiring for families' },
        { time: 'Day 5', name: 'Whale watching, Kaikōura 🐋', description: 'Near-guaranteed sperm whale sightings, plus dolphins and albatross year-round.', familyTag: '🐋 Once-in-a-lifetime moment' },
      ],
    },
  ],
  '🏖': [
    {
      id: 'mallorca',
      name: 'Mallorca',
      country: 'Spain',
      flag: '🇪🇸',
      emoji: '🏖',
      gradient: 'linear-gradient(135deg, #2a1a3a, #4a2a6a)',
      saving: '£185',
      ageRange: 'Ages 2–12',
      description: 'Clear shallow coves, waterparks, pirate boat trips and village markets. Mallorca is made for families with young children.',
      imageQuery: 'Mallorca beach cove turquoise water',
      facts: [
        { value: '2.5hrs', label: 'Flight time from UK' },
        { value: '26°C', label: 'Average sea temperature' },
        { value: '200+', label: 'Beaches & coves' },
        { value: '2+', label: 'Ideal minimum age' },
      ],
      loveIt: [
        'Shallow, calm coves toddlers can paddle in safely all day',
        'Short flight time means no jet lag, no meltdowns',
        'All-inclusive resorts with proper kids clubs from age 3',
        'Old town day trips are easy — even with buggies in tow',
      ],
      stats: { 'Flight time': '2.5 hrs', 'Sea temp': '26°C', 'Ideal age': '2–12' },
      deals: [
        {
          icon: '✈️', title: 'Return flights from UK airports',
          detail: 'easyJet / Jet2 direct · Many regional options',
          saving: 'Save £75',
          network: 'jet2', category: 'flight',
          baseUrl: 'https://www.jet2.com/holidays/mallorca',
          commissionModel: 'revenue_share', commissionValue: 0.015,
        },
        {
          icon: '🏨', title: 'All-inclusive family resort',
          detail: '4 pools · Kids club 3–12 · Splash zone',
          saving: 'Save £55/night',
          network: 'booking_com', category: 'hotel',
          baseUrl: 'https://www.booking.com/searchresults.html?ss=Mallorca+all+inclusive+family',
          commissionModel: 'revenue_share', commissionValue: 0.04,
        },
        {
          icon: '🚢', title: "Pirate boat trip, Port d'Alcúdia",
          detail: '2 hours · Kids pirate costume · Snorkelling stop',
          saving: 'Save £30/family',
          network: 'viator', category: 'activity',
          baseUrl: 'https://www.viator.com/Mallorca/d837-ttd/pirate-boat',
          commissionModel: 'revenue_share', commissionValue: 0.08,
        },
        {
          icon: '💧', title: 'Western Water Park family pass',
          detail: 'All rides included · Safe toddler zones',
          saving: 'Save £40/family',
          network: 'get_your_guide', category: 'activity',
          baseUrl: 'https://www.getyourguide.com/mallorca-l274/western-water-park-tickets',
          commissionModel: 'revenue_share', commissionValue: 0.08,
        },
      ],
      stops: [
        { time: 'Day 1', name: 'Playa de Muro beach 🏖', description: 'Shallow, calm Blue Flag beach. Toddlers and young kids paddle safely here all day.', familyTag: '🏖 Perfect for under-8s' },
        { time: 'Day 2', name: 'Western Water Park 💧', description: '35 slides and rides. The "Little West" section is built entirely for under-8s.', familyTag: '💧 Full family day' },
        { time: 'Day 3', name: "Pirate boat from Port d'Alcúdia 🏴‍☠️", description: 'Kids become pirates, boat stops for snorkelling in crystal coves. Crew entertains all afternoon.', familyTag: '🏴‍☠️ Kids remember this forever' },
        { time: 'Day 4', name: 'Caves of Drach 🦇', description: 'Lake inside a mountain, underground concert, rowing boats through enormous caverns.', familyTag: '🦇 Age 5+ love this' },
        { time: 'Day 5', name: 'Palma Old Town 🏛', description: "Horse carriage ride, ice cream by the cathedral, kids' lunch in the Born district.", familyTag: '🏛 Easy cultural half-day' },
      ],
    },
    {
      id: 'tenerife',
      name: 'Tenerife',
      country: 'Spain',
      flag: '🇪🇸',
      emoji: '🌴',
      gradient: 'linear-gradient(135deg, #1a3a2a, #2a5a3a)',
      saving: '£160',
      ageRange: 'Ages 2–12',
      description: "Whale watching, Siam Park (world's best waterpark), volcanic beaches and year-round sunshine for families.",
      imageQuery: 'Tenerife Siam Park waterpark volcanic beach',
      facts: [
        { value: '#1', label: 'Rated waterpark worldwide' },
        { value: '365', label: 'Days of sunshine a year' },
        { value: '4.5hrs', label: 'Flight time from UK' },
        { value: '2+', label: 'Ideal minimum age' },
      ],
      loveIt: [
        'Resident whales and dolphins spotted almost year-round',
        'Siam Park has toddler zones alongside the big-kid thrills',
        'Warm enough to swim in December — brilliant for half-term',
        'Loro Parque doubles as a full-day rainy-day backup plan',
      ],
      stats: { 'Flight time': '4.5 hrs', 'Year-round sun': 'Yes', 'Ideal age': '2–12' },
      deals: [
        {
          icon: '✈️', title: 'Return flights from UK',
          detail: 'TUI · Jet2 · Ryanair from 20 UK airports',
          saving: 'Save £60',
          network: 'tui', category: 'package',
          baseUrl: 'https://www.tui.co.uk/destinations/europe/spain/tenerife',
          commissionModel: 'revenue_share', commissionValue: 0.01,
        },
        {
          icon: '🏨', title: 'South Tenerife family hotel',
          detail: 'Animation team · Kids pool · Sea view rooms',
          saving: 'Save £45/night',
          network: 'booking_com', category: 'hotel',
          baseUrl: 'https://www.booking.com/searchresults.html?ss=Tenerife+south+family+hotel',
          commissionModel: 'revenue_share', commissionValue: 0.04,
        },
        {
          icon: '🌊', title: 'Siam Park family tickets',
          detail: "World's No.1 waterpark · All rides included",
          saving: 'Save £50/family',
          network: 'get_your_guide', category: 'activity',
          baseUrl: 'https://www.getyourguide.com/tenerife-l131/siam-park-tickets',
          commissionModel: 'revenue_share', commissionValue: 0.08,
        },
        {
          icon: '🐬', title: 'Whale & dolphin watching trip',
          detail: 'Near-guaranteed sightings · Marine biologist on board',
          saving: 'Save £25/family',
          network: 'viator', category: 'activity',
          baseUrl: 'https://www.viator.com/Tenerife/d786-ttd/whale-dolphin-watching',
          commissionModel: 'revenue_share', commissionValue: 0.08,
        },
      ],
      stops: [
        { time: 'Day 1', name: 'Siam Park 🌊', description: "The world's most awarded waterpark. Wave Palace, Tower of Power, and the lazy river kids adore.", familyTag: '🌊 Must-do for every family' },
        { time: 'Day 2', name: 'Whale & dolphin watching 🐬', description: 'Resident pilot whales and bottlenose dolphins live here year-round. Guaranteed sightings.', familyTag: '🐬 Magical for young children' },
        { time: 'Day 3', name: 'Teide National Park 🌋', description: 'Cable car to 3,500m. Snow possible in winter. Kids love the dramatic Martian landscape.', familyTag: '🌋 Extraordinary family moment' },
        { time: 'Day 4', name: 'Loro Parque 🦜', description: "Europe's best animal park — orcas, gorillas, tigers, penguins and the world's largest parrot collection.", familyTag: '🦜 Full family day' },
        { time: 'Day 5', name: 'Playa Las Teresitas 🏖', description: 'Golden Saharan-sand beach with calm sheltered water. Young children play safely all day.', familyTag: '🏖 Calm beach for toddlers' },
      ],
    },
    {
      id: 'bali',
      name: 'Bali',
      country: 'Indonesia',
      flag: '🇮🇩',
      emoji: '🌺',
      gradient: 'linear-gradient(135deg, #2a1a2a, #4a2a4a)',
      saving: '£200',
      ageRange: 'Ages 4–12',
      description: "Rice fields, temple monkeys, surf lessons and Asia's top waterpark — one of the great family islands.",
      imageQuery: 'Bali rice terraces',
      facts: [
        { value: '100+', label: 'Family-friendly resorts' },
        { value: '#1', label: 'Waterpark in Asia' },
        { value: '9', label: 'Recommended trip length (days)' },
        { value: '4+', label: 'Ideal minimum age' },
      ],
      loveIt: [
        'Private-pool villas often cost less than a UK hotel room',
        'Gentle beach breaks make surf lessons easy for first-timers',
        'Nannies and kids clubs are affordable and widely available',
        'Rice terraces and monkey forests turn walks into adventures',
      ],
      stats: { 'Flight time': '16 hrs', 'Family resorts': '100+', 'Best stay': '9 days' },
      deals: [
        {
          icon: '✈️', title: 'Return flights from London',
          detail: 'KLM / Singapore Airlines via hub',
          saving: 'Save £120',
          network: 'skyscanner', category: 'flight',
          baseUrl: 'https://www.skyscanner.net/transport/flights/lond/dps/',
          commissionModel: 'cpc', commissionValue: 0.65,
        },
        {
          icon: '🏨', title: 'Seminyak family villa with private pool',
          detail: 'Private pool · Breakfast included · Chef available',
          saving: 'Save £65/night',
          network: 'airbnb', category: 'hotel',
          baseUrl: 'https://www.airbnb.co.uk/s/Seminyak--Bali--Indonesia/homes?amenities[]=15',
          commissionModel: 'cpa', commissionValue: 35,
        },
        {
          icon: '🌊', title: 'Waterbom Bali family tickets',
          detail: "Asia's top waterpark · Lazy river included",
          saving: 'Save £35/family',
          network: 'klook', category: 'activity',
          baseUrl: 'https://www.klook.com/activity/waterbom-bali-water-park-ticket/',
          commissionModel: 'revenue_share', commissionValue: 0.06,
        },
        {
          icon: '🏄', title: 'Family surf lesson, Kuta Beach',
          detail: '2 hours · All ages · Boards & instructor included',
          saving: 'Save £20/family',
          network: 'get_your_guide', category: 'activity',
          baseUrl: 'https://www.getyourguide.com/kuta-l678/surf-lesson-family',
          commissionModel: 'revenue_share', commissionValue: 0.08,
        },
      ],
      stops: [
        { time: 'Day 1', name: 'Waterbom Bali 🌊', description: "Voted Asia's best waterpark. Flowrider, Climax slide and the lazy river for younger ones.", familyTag: '🌊 Best waterpark in Asia' },
        { time: 'Day 2', name: 'Sacred Monkey Forest, Ubud 🐒', description: 'Hundreds of wild macaques roam freely through an ancient temple forest. Kids are obsessed.', familyTag: '🐒 Kids are obsessed' },
        { time: 'Day 3', name: 'Family surf lesson, Kuta 🏄', description: 'Gentle beach break, patient instructors — most kids are standing by the second hour.', familyTag: '🏄 Age 5+ can do this' },
        { time: 'Day 4', name: 'Tegalalang rice terraces 📸', description: 'Walk the emerald terraces, swing from the famous Bali swings and spot the ducks below.', familyTag: '📸 Beautiful for the whole family' },
        { time: 'Day 5', name: 'Tirta Empul temple 🙏', description: 'Watch Balinese purification rituals in a living, breathing ancient temple.', familyTag: '🙏 Real Bali for curious kids' },
      ],
    },
  ],
  '🏯': [
    {
      id: 'japan',
      name: 'Japan',
      country: 'Japan',
      flag: '🇯🇵',
      emoji: '🏯',
      gradient: 'linear-gradient(135deg, #2a1a2a, #4a2a4a)',
      saving: '£265',
      ageRange: 'Ages 5–12',
      description: 'Samurai castles, bullet trains, robot restaurants and the cleanest, safest cities on earth for families.',
      imageQuery: 'Kyoto temple autumn bullet train Japan',
      facts: [
        { value: '#1', label: 'Safest country for families' },
        { value: '320km/h', label: 'Bullet train top speed' },
        { value: '800', label: 'Friendly deer in Nara Park' },
        { value: '10', label: 'Recommended trip length (days)' },
      ],
      loveIt: [
        'Trains run so precisely, kids learn to read a timetable',
        'Vending machines and konbini stores solve every snack crisis',
        'Spotless, safe streets even late in busy neighbourhoods',
        'TeamLab and Nara deer park make even jet-lagged toddlers grin',
      ],
      stats: { 'Safety': 'World #1', 'Bullet trains': 'Yes', 'Best stay': '10 days' },
      deals: [
        {
          icon: '✈️', title: 'Return flights from London',
          detail: 'ANA / JAL direct to Tokyo',
          saving: 'Save £120',
          network: 'skyscanner', category: 'flight',
          baseUrl: 'https://www.skyscanner.net/transport/flights/lond/tyoa/',
          commissionModel: 'cpc', commissionValue: 0.65,
        },
        {
          icon: '🏨', title: 'Family hotel in Shinjuku',
          detail: 'Traditional futon option · Central location',
          saving: 'Save £55/night',
          network: 'booking_com', category: 'hotel',
          baseUrl: 'https://www.booking.com/searchresults.html?ss=Shinjuku+Tokyo+family+hotel',
          commissionModel: 'revenue_share', commissionValue: 0.04,
        },
        {
          icon: '🚅', title: 'JR Pass family 14-day',
          detail: 'All bullet trains · Kids half price',
          saving: 'Save £90/family',
          network: 'viator', category: 'transfer',
          baseUrl: 'https://www.viator.com/Japan/d334-ttd/JR-Pass',
          commissionModel: 'revenue_share', commissionValue: 0.08,
        },
        {
          icon: '🌌', title: 'TeamLab Planets family tickets',
          detail: 'Priority entry · Child-safe immersive art',
          saving: 'Save £28/family',
          network: 'klook', category: 'activity',
          baseUrl: 'https://www.klook.com/activity/teamlab-planets-tokyo-ticket/',
          commissionModel: 'revenue_share', commissionValue: 0.06,
        },
      ],
      stops: [
        { time: 'Day 1', name: 'TeamLab Planets, Tokyo 🌌', description: 'Rooms of infinite flowers and mirror universes. Children are completely speechless.', familyTag: '🌌 Age 4+ will love this' },
        { time: 'Day 2', name: 'Bullet train to Kyoto 🚅', description: 'The Shinkansen itself is an event — kids press faces to the window at 320km/h.', familyTag: '🚅 Kids talk about the train' },
        { time: 'Day 3', name: 'Arashiyama Bamboo Grove 🎋', description: 'Towering bamboo, monkey park, kimono rental and a river boat. A full sensory day.', familyTag: '🎋 Magical for families' },
        { time: 'Day 4', name: 'Osaka Aquarium Kaiyukan 🐠', description: "World's largest Pacific Ocean tank. Whale sharks, rays, sea otters — hours of wonder.", familyTag: '🐠 Outstanding for under-12s' },
        { time: 'Day 5', name: 'Nara deer park 🦌', description: '800 friendly deer roam freely through the city — you can hand-feed them. Kids are delighted.', familyTag: '🦌 Under-10s completely love it' },
      ],
    },
    {
      id: 'portugal',
      name: 'Portugal',
      country: 'Portugal',
      flag: '🇵🇹',
      emoji: '🏛',
      gradient: 'linear-gradient(135deg, #2a2a1a, #4a4a1a)',
      saving: '£150',
      ageRange: 'Ages 3–12',
      description: "Fairy-tale palaces in Sintra, safe Atlantic beaches, food kids actually eat and Europe's shortest flights.",
      imageQuery: 'Sintra Pena Palace Portugal colourful castle',
      facts: [
        { value: '2.5hrs', label: 'Flight time from UK' },
        { value: '8', label: 'Fairy-tale palaces in Sintra' },
        { value: '26°C', label: 'Average summer high' },
        { value: '3+', label: 'Ideal minimum age' },
      ],
      loveIt: [
        'Pena Palace looks straight out of a children’s storybook',
        'Custard tarts and simple grilled food even fussy eaters love',
        'Calm Atlantic beaches 30 minutes from the airport',
        'One of Europe’s cheapest city breaks for a family of four',
      ],
      stats: { 'Flight time': '2.5 hrs', 'Palaces in Sintra': '8', 'Ideal age': '3–12' },
      deals: [
        {
          icon: '✈️', title: 'Return flights from UK',
          detail: 'TAP · easyJet · Ryanair from many airports',
          saving: 'Save £55',
          network: 'skyscanner', category: 'flight',
          baseUrl: 'https://www.skyscanner.net/transport/flights/uk/lis/',
          commissionModel: 'cpc', commissionValue: 0.65,
        },
        {
          icon: '🏨', title: 'Lisbon family apartment',
          detail: 'Full kitchen · Old town · Lift access',
          saving: 'Save £40/night',
          network: 'airbnb', category: 'hotel',
          baseUrl: 'https://www.airbnb.co.uk/s/Lisbon--Portugal/homes?amenities[]=15',
          commissionModel: 'cpa', commissionValue: 25,
        },
        {
          icon: '🏯', title: 'Sintra palaces family ticket',
          detail: 'Pena Palace + Moorish Castle · Kids audio guide',
          saving: 'Save £24/family',
          network: 'get_your_guide', category: 'activity',
          baseUrl: 'https://www.getyourguide.com/sintra-l747/pena-palace-tickets',
          commissionModel: 'revenue_share', commissionValue: 0.08,
        },
        {
          icon: '🔦', title: 'Quinta da Regaleira family tour',
          detail: 'Secret tunnels · Underground wells · Child-friendly guide',
          saving: 'Save £18/family',
          network: 'viator', category: 'activity',
          baseUrl: 'https://www.viator.com/Sintra/d4385-ttd/Quinta-da-Regaleira',
          commissionModel: 'revenue_share', commissionValue: 0.08,
        },
      ],
      stops: [
        { time: 'Day 1', name: 'Pena Palace, Sintra 🏯', description: 'A Disney castle on a real mountain. Yellow and red turrets, dungeons and a moat. Children are entranced.', familyTag: '🏯 Fairy tale for young children' },
        { time: 'Day 2', name: 'Cascais beach 🏖', description: 'Calm, sandy Atlantic beaches 30 minutes from Lisbon. Rock pools and safe swimming for all ages.', familyTag: '🏖 Easy family beach day' },
        { time: 'Day 3', name: 'Lisbon Oceanarium 🦈', description: "Europe's finest aquarium — an open ocean tank visible from all four sides. Rays, sharks and sunfish.", familyTag: '🦈 Best aquarium in Europe' },
        { time: 'Day 4', name: 'Quinta da Regaleira 🔦', description: 'Secret tunnels, underground wells and hidden grottos — children become real explorers.', familyTag: '🔦 Young adventurers love this' },
        { time: 'Day 5', name: 'Pastéis de Belém & waterfront 🥧', description: "Watch monks make custard tarts in the original 1837 bakery. Then Belém Tower.", familyTag: '🥧 Delicious cultural morning' },
      ],
    },
    {
      id: 'rome',
      name: 'Rome',
      country: 'Italy',
      flag: '🇮🇹',
      emoji: '🏛',
      gradient: 'linear-gradient(135deg, #2a1a1a, #5a2a1a)',
      saving: '£145',
      ageRange: 'Ages 5–12',
      description: 'Gladiators, underground catacombs, the best gelato on earth and 2,800 years of history children can actually touch.',
      imageQuery: 'Rome Colosseum gladiator ancient ruins',
      facts: [
        { value: '2,800', label: 'Years of history' },
        { value: '2.5hrs', label: 'Flight time from UK' },
        { value: '6', label: 'Gelato-worthy neighbourhoods' },
        { value: '5+', label: 'Ideal minimum age' },
      ],
      loveIt: [
        'Skip-the-queue gladiator tours turn history into a game',
        'Everything is walkable — no car needed for the whole trip',
        'Pasta-making classes double as a rainy-afternoon activity',
        'Gelato really is on every corner, and it really is that good',
      ],
      stats: { 'Flight time': '2.5 hrs', 'History': '2,800 years', 'Ideal age': '5–12' },
      deals: [
        {
          icon: '✈️', title: 'Return flights from UK',
          detail: 'BA · easyJet · Ryanair direct',
          saving: 'Save £60',
          network: 'skyscanner', category: 'flight',
          baseUrl: 'https://www.skyscanner.net/transport/flights/uk/rome/',
          commissionModel: 'cpc', commissionValue: 0.65,
        },
        {
          icon: '🏨', title: 'Family hotel near Piazza Navona',
          detail: 'Breakfast · Cots available · Central location',
          saving: 'Save £45/night',
          network: 'booking_com', category: 'hotel',
          baseUrl: 'https://www.booking.com/searchresults.html?ss=Rome+city+centre+family+hotel',
          commissionModel: 'revenue_share', commissionValue: 0.04,
        },
        {
          icon: '🏛', title: 'Colosseum skip-the-queue family tour',
          detail: 'Kids gladiator programme included',
          saving: 'Save £20/family',
          network: 'viator', category: 'activity',
          baseUrl: 'https://www.viator.com/Rome/d511-ttd/Colosseum-skip-the-line',
          commissionModel: 'revenue_share', commissionValue: 0.08,
        },
        {
          icon: '🍝', title: 'Family pasta-making class',
          detail: '2 hours · Everything included · Kids keep apron',
          saving: 'Save £25/family',
          network: 'get_your_guide', category: 'activity',
          baseUrl: 'https://www.getyourguide.com/rome-l33/pasta-making-class-family',
          commissionModel: 'revenue_share', commissionValue: 0.08,
        },
      ],
      stops: [
        { time: 'Day 1', name: 'Colosseum gladiator experience 🛡', description: 'Kids get gladiator names, learn fighting stances and walk where 50,000 Romans once roared.', familyTag: '🛡 Age 5+ completely gripped' },
        { time: 'Day 2', name: "Vatican Museums with kids' guide 🎨", description: "Egyptian mummies and armour first — then Michelangelo's Sistine ceiling.", familyTag: "🎨 Do this with a kids' guide" },
        { time: 'Day 3', name: 'Borghese Gardens & rowing boats 🚣', description: 'Rent rowing boats on the lake, then explore the park by pedal cart. Perfect pace for young kids.', familyTag: '🚣 Perfect for young children' },
        { time: 'Day 4', name: 'Family pasta-making class 🍝', description: 'An Italian grandmother teaches the whole family. Lunch is the delicious result.', familyTag: '🍝 Greatest family activity in Rome' },
        { time: 'Day 5', name: 'Trevi Fountain & gelato trail 🍦', description: 'Throw your coins, then follow the gelato trail through 6 Roman neighbourhoods.', familyTag: "🍦 Kids' favourite day in Rome" },
      ],
    },
  ],
}

export { DESTINATIONS }

export const TICKER_PLACES = [
  'Orlando 🎢', 'Bali 🌺', 'Japan 🏯', 'Kenya 🦁',
  'Mallorca 🏖', 'Costa Rica 🦥', 'New Zealand 🏔', 'Portugal 🏛',
  'Singapore 🌆', 'Tenerife 🌴', 'Dubai 🏙', 'Greece 🏛',
  'Australia 🦘', 'Canada 🍁',
]
